import { useEffect } from "react";

import { DashboardWindow } from "./dashboardHelpers";

/**
 * Closes the mobile block title/description popovers when the user taps anywhere outside them.
 *
 * Both popovers are native `<details>`/`<summary>` disclosures (see blockDescriptionHtml.ts), which
 * stay open until the summary is tapped again — there is no built-in outside-dismiss. That markup is
 * an innerHTML string injected by useBlockRender with no React tree to attach handlers to, so this
 * is a delegated listener rather than per-element wiring.
 *
 * `pointerdown`, not `click`: on iOS a tap on a non-interactive element (a chart, a card, empty
 * space) does not reliably produce a `click` that reaches a delegated document listener, which is
 * exactly where most outside taps land. `pointerdown` fires for any tap regardless of what was hit.
 *
 * CAPTURE phase, because dashboard blocks contain third-party widgets — AG Grid in particular — that
 * call stopPropagation() on pointer events. In the bubble phase an outside tap landing inside a grid
 * could be swallowed before reaching the document, leaving the popover stuck open.
 *
 * A tap INSIDE an open disclosure is left alone: `contains()` covers both the popover (so text can
 * be selected without it vanishing) and the summary (whose native toggle then closes it — closing it
 * here too would double-toggle it straight back open).
 *
 * Two documents are watched. The iframe's own document catches the common case, but the dashboard
 * chrome — navbar, kebab menu — lives in the TOP-LEVEL document, and events there never propagate
 * into the iframe. Without the second listener, tapping the navbar would leave a popover open over
 * the dashboard. The two never double-fire: a tap inside the iframe does not bubble out to the
 * parent document.
 */
export const useCloseBlockPopoversOnOutsideTap = (iframeWindow: DashboardWindow | null): void => {
  useEffect(() => {
    if (!iframeWindow) {
      return;
    }

    const iframeDocument = iframeWindow.document;

    const closePopoversOutside = (event: Event) => {
      const openDisclosures = iframeDocument.querySelectorAll<HTMLDetailsElement>(
        ".block-title-row details[open]"
      );
      if (!openDisclosures.length) {
        return;
      }
      // Duck-typed on nodeType rather than `instanceof Node`, which is WRONG ACROSS REALMS and was
      // silently breaking the common case. This module is bundled in the parent document, so `Node`
      // resolves to the parent window's constructor, while a tap inside the dashboard lands on an
      // element belonging to the iframe's realm — `instanceof` is false for every one of them. That
      // sent each in-iframe tap down the `!target` branch, which closes EVERY open disclosure,
      // including the one the tap was inside. Tapping a summary to close its own popover therefore
      // closed it here on pointerdown and the click immediately re-opened it, so it never closed.
      //
      // A tap in the parent document still has no node in the iframe's tree, so `contains` returns
      // false there and the original "outside closes everything" behaviour is unchanged.
      const eventTarget = event.target as Node | null;
      const target = eventTarget && typeof eventTarget.nodeType === "number" ? eventTarget : null;
      openDisclosures.forEach(disclosure => {
        if (!target || !disclosure.contains(target)) {
          disclosure.open = false;
        }
      });
    };

    iframeDocument.addEventListener("pointerdown", closePopoversOutside, true);
    document.addEventListener("pointerdown", closePopoversOutside, true);

    return () => {
      iframeDocument.removeEventListener("pointerdown", closePopoversOutside, true);
      document.removeEventListener("pointerdown", closePopoversOutside, true);
    };
  }, [iframeWindow]);
};
